import { useState } from "react";
import api, { apiErr, photoUrl } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { EDITOR } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["kg", "t", "m³", "m²"];
export const BLANK_LINE = { material: "", qty: 0, unit: "kg", collection_date: "", pathway: "Recycle", destination: "", vehicle: "" };

function LineRow({ i, line, set, remove, pathways, disabled }) {
  const f = (k) => (e) => set({ ...line, [k]: e.target.value });
  return (
    <div className="rounded-lg border border-sage-light bg-white p-3" data-testid={EDITOR.line(i)}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Input className="col-span-2" placeholder="Material" value={line.material} onChange={f("material")} disabled={disabled} data-testid={EDITOR.lineField(i, "material")} />
        <Input type="number" min="0" placeholder="Qty" value={line.qty} onChange={f("qty")} disabled={disabled} data-testid={EDITOR.lineField(i, "qty")} />
        <Select value={line.unit} onValueChange={(v) => set({ ...line, unit: v })} disabled={disabled}>
          <SelectTrigger data-testid={EDITOR.lineField(i, "unit")}><SelectValue /></SelectTrigger>
          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={line.pathway} onValueChange={(v) => set({ ...line, pathway: v })} disabled={disabled}>
          <SelectTrigger className="col-span-2" data-testid={EDITOR.lineField(i, "pathway")}><SelectValue /></SelectTrigger>
          <SelectContent>{pathways.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Input className="col-span-2" placeholder="Destination facility" value={line.destination || ""} onChange={f("destination")} disabled={disabled} data-testid={EDITOR.lineField(i, "destination")} />
        <Input className="col-span-2" placeholder="Vehicle / trip" value={line.vehicle || ""} onChange={f("vehicle")} disabled={disabled} data-testid={EDITOR.lineField(i, "vehicle")} />
        <Input type="date" value={line.collection_date || ""} onChange={f("collection_date")} disabled={disabled} data-testid={EDITOR.lineField(i, "collection_date")} />
        {!disabled && <Button type="button" variant="ghost" size="sm" className="text-rose-500" onClick={remove} data-testid={EDITOR.removeLine(i)}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function EvidenceBlock({ evidence, setEvidence, kinds, disabled }) {
  const [kind, setKind] = useState("weighing_slip");
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    for (const file of files) {
      try {
        const r = await api.post("/photos", { data_url: await compressImage(file), caption: kinds[kind] });
        setEvidence((prev) => [...prev, { photo_id: r.data.id, kind, caption: kinds[kind] }]);
      } catch (err) { toast.error(apiErr(err)); }
    }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {evidence.map((ev, i) => (
          <div key={ev.photo_id} className="relative w-20 text-center" data-testid={EDITOR.evidenceItem(i)}>
            <img src={photoUrl({ id: ev.photo_id })} alt="" className="h-16 w-20 rounded border border-sage-light object-cover" />
            <p className="truncate text-[10px] text-muted-foreground">{kinds[ev.kind] || ev.kind}</p>
            {!disabled && <button type="button" onClick={() => setEvidence((p) => p.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-500 p-0.5 text-white" data-testid={EDITOR.removeEvidence(i)}><Trash2 className="h-3 w-3" /></button>}
          </div>
        ))}
        {evidence.length === 0 && <p className="text-xs text-muted-foreground">No evidence attached.</p>}
      </div>
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-9 w-[210px]" data-testid={EDITOR.evidenceKind}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(kinds).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-sage-light px-3 text-sm text-forest-dark hover:bg-sage-surface">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Upload image
            <input type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={EDITOR.evidenceUpload} />
          </label>
        </div>
      )}
    </div>
  );
}

export function ReportEditor({ value, onChange, pathways, kinds, disabled }) {
  const lines = value.lines || [];
  const setLines = (ls) => onChange({ ...value, lines: ls });
  const setEvidence = (fn) => onChange((v) => ({ ...v, evidence: typeof fn === "function" ? fn(v.evidence || []) : fn }));
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between"><Label>Waste lines</Label>
          {!disabled && <Button type="button" size="sm" variant="outline" onClick={() => setLines([...lines, { ...BLANK_LINE }])} data-testid={EDITOR.addLine}><Plus className="mr-1 h-4 w-4" /> Add line</Button>}
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => <LineRow key={l.id || i} i={i} line={l} pathways={pathways} disabled={disabled}
            set={(nl) => setLines(lines.map((x, j) => (j === i ? nl : x)))} remove={() => setLines(lines.filter((_, j) => j !== i))} />)}
          {lines.length === 0 && <p className="text-xs text-muted-foreground">No waste lines yet.</p>}
        </div>
      </div>
      <div><Label className="mb-2 block">Evidence (photos, weighing slips, receipts, licences)</Label>
        <EvidenceBlock evidence={value.evidence || []} setEvidence={setEvidence} kinds={kinds} disabled={disabled} /></div>
      <div className="space-y-1.5"><Label>Notes</Label>
        <Textarea rows={2} value={value.notes || ""} onChange={(e) => onChange({ ...value, notes: e.target.value })} disabled={disabled} data-testid={EDITOR.notes} /></div>
    </div>
  );
}

export const toPayload = (v) => ({
  lines: (v.lines || []).map((l) => ({ ...l, qty: Number(l.qty) || 0, collection_date: l.collection_date || null })),
  evidence: v.evidence || [], notes: v.notes || "",
});

export const PATHWAYS = ["Avoid", "Reduce", "Reuse", "Upcycle", "Recycle", "Recover", "Dispose"];
export const EVIDENCE_KINDS = { photo: "Site photo", weighing_slip: "Weighing slip", disposal_receipt: "Disposal receipt", licence: "Carrier / facility licence" };
